import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { AdditionalFields } from '../../models/models';
import { getDateModified, getRoleForMutation } from '../../utilities/utilityFunctions';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface SavepmChecklist {
  readonly inspectionId: number;
  readonly assetCategoryInspectionTypeCheckListId: number;
  readonly isCompleted: boolean | null;
  readonly comments: string;
  readonly dateCreated?: string;
  readonly dateModified?: string;
  readonly createdBy?: string;
  readonly modifiedBy?: string;
  readonly isActive: boolean;
  readonly pk_uuid: string;
  readonly pkAssetInspectionPMChecklist?: number;
  readonly site?: number;
  readonly discipline?: number;
  readonly inspectorId?: number;
  readonly fkAssetInspectionTask: number;
}

export const savePmChecklistSchema: RxJsonSchema<SavepmChecklist> = {
  type: 'object',
  version: 0,
  description: 'describes PM Checklist',
  primaryKey: 'pk_uuid',
  properties: {
    inspectionId: { type: ['number', 'null'] },
    assetCategoryInspectionTypeCheckListId: { type: ['number', 'null'] },
    isCompleted: { type: ['boolean', 'null'] },
    comments: { type: ['string', 'null'] },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    createdBy: { type: ['string', 'null'] },
    modifiedBy: { type: ['string', 'null'] },
    isActive: { type: ['boolean', 'null'] },
    pk_uuid: { type: 'string', maxLength: 36 },
    pkAssetInspectionPMChecklist: { type: ['number', 'null'] },
    site: { type: ['number', 'null'] },
    discipline: { type: ['number', 'null'] },
    inspectorId: { type: ['number', 'null'] },
    fkAssetInspectionTask: { type: ['number', 'null'] },
  },
};

export type SavePmChecklistCollection = RxCollection<SavepmChecklist> | null;
export type SavePmChecklistDocument = RxDocument<SavepmChecklist>;

export const savePmChecklistPullQueryBuilder = (document: SavepmChecklist) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query mobileAssetInspectionPMChecklistsInfo(
      $siteId: Int!
      $disciplineId: Int!
      $lastRequested: DateTime!
      $isLeadInspector: Boolean!
    ) {
      mobileAssetInspectionPMChecklistsInfo(
        siteId: $siteId
        disciplineId: $disciplineId
        lastRequested: $lastRequested
        isLeadInspector: $isLeadInspector
      ) {
        inspectionId
        assetCategoryInspectionTypeCheckListId
        isCompleted
        comments
        dateCreated
        dateModified
        createdBy
        modifiedBy
        isActive
        pk_uuid
        pkAssetInspectionPMChecklist
        site
        discipline
        inspectorId
        fkAssetInspectionTask
      }
    }
  `);

  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastRequested: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc.site,
        doc.discipline,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
      isLeadInspector: getValueFromLocalStorage('isLeadInspector') === 'True',
    },
  };
};

export const savePmChecklistPushQueryBuilder = (doc: SavePmChecklistDocument[]) => {
  const document = doc[0];
  const query = print(gql`
    mutation saveUpdateMobileAssetInspectionPMChecklist(
      $role: Int!
      $AssetInspectionPMChecklistInput: AssetInspectionPMChecklistInput
    ) {
      saveUpdateMobileAssetInspectionPMChecklist(
        role: $role
        assetInspectionPMChecklist: $AssetInspectionPMChecklistInput
      ) {
        id
        pk_uuid
        status
      }
    }
  `);

  const role = getRoleForMutation(document);

  const variables = {
    role,
    AssetInspectionPMChecklistInput: document,
  };
  return {
    query,
    variables,
  };
};

export const savePmChecklistPushModifier = (doc: SavePmChecklistDocument) => {
  const obj: SavePmChecklistDocument &
    AdditionalFields & { pkAssetInspectionPMChecklist?: number } = {
    ...doc,
  };
  if (obj.pkAssetInspectionPMChecklist === 0) {
    obj.pkAssetInspectionPMChecklist = undefined;
  }
  const blackListedProp = [
    '_attachments',
    '_rev',
    '_deleted',
    'site',
    'discipline',
    'inspectorId',
    '_meta',
    'isDeleted',
  ];
  blackListedProp.forEach(property => {
    obj[property as keyof AdditionalFields] = undefined;
  });
  return obj;
};

export default {};
